/**
 * How dates, doses, money, durations, lists and statuses read on screen, once, so the same order
 * date or dose reads the same on the account, the clinic portal and the admin.
 *
 * Everything is US English, because Lattice sells in the US. A date with a time is shown in the
 * viewer's time zone unless a test or a caller passes one. A bare calendar date such as a birth or
 * collection date has no zone, so it is read and shown as UTC and never slides to the day before
 * for a reader west of Greenwich.
 */
import type {
  AdminExtractionJob,
  ClinicLinkStatus,
  ClinicalReviewState,
  FormulaVersionState,
  OrderState,
} from "@lattice/core";

/** What a screen shows for a date the record does not have. */
export const NOT_RECORDED = "Not recorded";

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

const DAY: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

/*
  Building an Intl formatter costs far more than using one, and a table formats the same few
  shapes hundreds of times, so each shape and zone is built once.
*/
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  const known = formatters.get(key);

  if (known) return known;
  const made = new Intl.DateTimeFormat("en-US", options);

  formatters.set(key, made);

  return made;
}

function zoned(timeZone: string | undefined): Intl.DateTimeFormatOptions {
  return timeZone === undefined ? {} : { timeZone };
}

/**
 * `Oct 20, 2026`. A bare `YYYY-MM-DD` is shown as that calendar day. A timestamp is shown in
 * `timeZone`, or the viewer's zone when none is given. Null reads "Not recorded", and a value that
 * is not a date comes back unchanged rather than as "Invalid Date".
 */
export function formatDate(value: string | null, timeZone?: string): string {
  if (value === null) return NOT_RECORDED;
  const calendar = CALENDAR_DATE.test(value);
  const date = new Date(calendar ? `${value}T00:00:00Z` : value);

  if (Number.isNaN(date.getTime())) return value;

  return formatter({ ...DAY, ...(calendar ? { timeZone: "UTC" } : zoned(timeZone)) }).format(date);
}

/**
 * `Oct 20, 2026, 11:55 AM`. Staff screens add the zone abbreviation (`11:55 AM PDT`), because staff
 * in different zones compare times in tickets. Customer screens leave it off.
 */
export function formatDateTime(
  value: string,
  audience: "staff" | "customer",
  timeZone?: string,
): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  /*
    The date and the time are formatted apart and joined with a comma here. One formatter asked for
    both picks its own joiner per engine, and under Bun it writes " at ".
  */
  const time: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    ...zoned(timeZone),
  };

  if (audience === "staff") time.timeZoneName = "short";

  return `${formatter({ ...DAY, ...zoned(timeZone) }).format(date)}, ${formatter(time).format(date)}`;
}

const MINUTE = 60_000;

const HOUR = 60 * MINUTE;

const DAY_MS = 24 * HOUR;

/** The calendar day a moment falls on in a zone, as `YYYY-MM-DD`. */
function dayKey(date: Date, timeZone: string | undefined): string {
  return formatter({
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...zoned(timeZone),
  }).format(date);
}

/**
 * For the last seven days in a staff table: `Just now`, `12m ago`, `3h ago`, `Yesterday`,
 * `4 days ago`. Anything older, or in the future, is the plain date. "Yesterday" is the previous
 * calendar day in the zone, so a moment 20 hours ago that crossed midnight reads "Yesterday".
 */
export function formatRelative(value: string, now: Date, timeZone?: string): string {
  const date = new Date(value);
  const elapsed = now.getTime() - date.getTime();

  if (Number.isNaN(elapsed) || elapsed < 0 || elapsed >= 7 * DAY_MS)
    return formatDate(value, timeZone);

  if (elapsed < MINUTE) return "Just now";

  if (elapsed < HOUR) return `${String(Math.floor(elapsed / MINUTE))}m ago`;

  const yesterday = dayKey(new Date(now.getTime() - DAY_MS), timeZone);

  if (dayKey(date, timeZone) === dayKey(now, timeZone))
    return `${String(Math.floor(elapsed / HOUR))}h ago`;

  if (dayKey(date, timeZone) === yesterday) return "Yesterday";

  return `${String(Math.max(2, Math.round(elapsed / DAY_MS)))} days ago`;
}

/** `Apr 12, 1986` from `1986-04-12`. Null reads "Not recorded". */
export function formatBirthDate(value: string | null): string {
  return formatDate(value);
}

const DOSE = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/** `1,000 mcg`, `2.5 g`. At most two decimals and no trailing zeros. */
export function formatDose(amount: number, unit: string): string {
  return `${DOSE.format(amount)} ${unit}`;
}

/** `$129.00` from 12900 cents. The currency comes from the record. */
export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

/** `46 days` from two days up, `18 h` below that, `25 min` below an hour. */
export function formatDuration(ms: number): string {
  if (ms >= 2 * DAY_MS) return `${String(Math.floor(ms / DAY_MS))} days`;

  if (ms >= HOUR) return `${String(Math.floor(ms / HOUR))} h`;

  return `${String(Math.max(0, Math.floor(ms / MINUTE)))} min`;
}

const LIST = new Intl.ListFormat("en-US", { style: "long", type: "conjunction" });

/** `Vitamin D, magnesium, and zinc`. */
export function formatList(items: ReadonlyArray<string>): string {
  return LIST.format(items);
}

/** A pharmacy-facing order's state. */
export const ORDER_STATUS_LABEL: Readonly<Record<OrderState, string>> = {
  pending_payment: "Pending payment",
  paid: "Paid",
  released: "Released",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

/** A formula version's state. */
export const FORMULA_STATUS_LABEL: Readonly<Record<FormulaVersionState, string>> = {
  draft: "Draft",
  in_review: "In review",
  approved: "Approved",
  declined: "Declined",
  superseded: "Replaced",
};

/** A physician review's state. */
export const REVIEW_STATUS_LABEL: Readonly<Record<ClinicalReviewState, string>> = {
  waiting: "Waiting",
  in_progress: "In progress",
  approved: "Approved",
  edited: "Approved with changes",
  declined: "Declined",
};

/** A clinic's link to a patient. */
export const LINK_STATUS_LABEL: Readonly<Record<ClinicLinkStatus, string>> = {
  pending: "Pending",
  active: "Active",
  revoked: "Removed",
};

/** A lab extraction job. */
export const JOB_STATUS_LABEL: Readonly<Record<AdminExtractionJob["status"], string>> = {
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
};

/** How a state reads as a pill: its words and a tone. `StatusPill` in @lattice/ui draws it. */
export interface Pill {
  readonly label: string;
  readonly tone: "neutral" | "ok" | "warn" | "danger" | "info";
}

const ORDER_TONE: Readonly<Record<OrderState, Pill["tone"]>> = {
  pending_payment: "warn",
  paid: "info",
  released: "ok",
  cancelled: "neutral",
  refunded: "neutral",
};

const FORMULA_TONE: Readonly<Record<FormulaVersionState, Pill["tone"]>> = {
  draft: "neutral",
  in_review: "warn",
  approved: "ok",
  declined: "danger",
  superseded: "neutral",
};

const REVIEW_TONE: Readonly<Record<ClinicalReviewState, Pill["tone"]>> = {
  waiting: "warn",
  in_progress: "info",
  approved: "ok",
  edited: "ok",
  declined: "danger",
};

const LINK_TONE: Readonly<Record<ClinicLinkStatus, Pill["tone"]>> = {
  pending: "warn",
  active: "ok",
  revoked: "neutral",
};

const JOB_TONE: Readonly<Record<AdminExtractionJob["status"], Pill["tone"]>> = {
  running: "info",
  succeeded: "ok",
  failed: "danger",
};

export const orderPill = (state: OrderState): Pill => ({
  label: ORDER_STATUS_LABEL[state],
  tone: ORDER_TONE[state],
});

export const formulaPill = (state: FormulaVersionState): Pill => ({
  label: FORMULA_STATUS_LABEL[state],
  tone: FORMULA_TONE[state],
});

export const reviewPill = (state: ClinicalReviewState): Pill => ({
  label: REVIEW_STATUS_LABEL[state],
  tone: REVIEW_TONE[state],
});

export const linkPill = (state: ClinicLinkStatus): Pill => ({
  label: LINK_STATUS_LABEL[state],
  tone: LINK_TONE[state],
});

export const jobPill = (state: AdminExtractionJob["status"]): Pill => ({
  label: JOB_STATUS_LABEL[state],
  tone: JOB_TONE[state],
});
